import { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Sparkles, Zap, TrendingUp, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '../ui/Button';
import { useAIOpsSession } from '../../hooks/useAIOpsSession';
import { executeAI } from '../../lib/ai/orchestrator';

interface AIOpsCopilotPanelProps {
  isOpen: boolean;
  onClose: () => void;
  context: {
    route: string;
    tenantId: string;
    extra?: Record<string, any>;
  };
}

export function AIOpsCopilotPanel({ isOpen, onClose, context }: AIOpsCopilotPanelProps) {
  const { messages, isProcessing, setIsProcessing, addMessage } = useAIOpsSession();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage('system', `AI Copilot ready. Current context: ${context.route}`);
    }
  }, [isOpen]);

  const quickActions = [
    { label: 'Explain this page', icon: Sparkles, prompt: `Explain what I can do on the ${context.route} page` },
    { label: 'Show insights', icon: TrendingUp, prompt: 'What are the key insights I should focus on right now?' },
    { label: 'Find issues', icon: AlertCircle, prompt: 'Are there any issues or anomalies I should be aware of?' },
    { label: 'Suggest actions', icon: Zap, prompt: 'What actions should I take next to improve operations?' },
  ];

  const handleSend = async (prompt?: string) => {
    const messageText = prompt || input.trim();
    if (!messageText || isProcessing) return;

    setInput('');
    addMessage('user', messageText);
    setIsProcessing(true);

    try {
      const response = await executeAI(context.tenantId, {
        prompt: messageText,
        context: {
          route: context.route,
          ...context.extra,
        },
      });

      addMessage('assistant', response.content);
    } catch (error: any) {
      addMessage('assistant', `Error: ${error.message || 'Failed to process request'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={clsx(
        'fixed top-0 right-0 h-full w-full md:w-[500px] glass-panel border-l border-white/10 flex flex-col z-40 transition-transform duration-300',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
    >
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-400" />
          <h2 className="text-lg font-semibold text-white">AI Copilot</h2>
          <span className="px-2 py-0.5 text-xs font-bold rounded bg-primary-500/20 text-primary-400">
            BETA
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors"
        >
          <X className="w-5 h-5 text-white/60" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={clsx(
              'flex gap-3',
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            {message.role !== 'user' && (
              <div className="w-8 h-8 rounded-full bg-primary-500/20 border border-primary-400/30 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-primary-400" />
              </div>
            )}
            <div
              className={clsx(
                'max-w-[80%] p-3 rounded-xl',
                message.role === 'user'
                  ? 'bg-primary-500/30 border border-primary-400/50 text-white'
                  : message.role === 'system'
                  ? 'bg-white/5 border border-white/10 text-white/60 text-sm italic'
                  : 'bg-white/5 border border-white/10 text-white'
              )}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
            </div>
            {message.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-semibold text-white">U</span>
              </div>
            )}
          </div>
        ))}
        {isProcessing && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary-500/20 border border-primary-400/30 flex items-center justify-center flex-shrink-0">
              <Loader2 className="w-4 h-4 text-primary-400 animate-spin" />
            </div>
            <div className="max-w-[80%] p-3 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-white/60">Thinking...</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {messages.length <= 1 && (
        <div className="p-4 border-t border-white/10">
          <p className="text-xs text-white/60 mb-3">Quick Actions:</p>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={() => handleSend(action.prompt)}
                disabled={isProcessing}
                className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary-400/30 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <action.icon className="w-4 h-4 text-primary-400 flex-shrink-0" />
                <span className="text-xs text-white truncate">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask AI Copilot anything..."
            disabled={isProcessing}
            className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-primary-400/50 transition-colors disabled:opacity-50"
          />
          <Button
            onClick={() => handleSend()}
            disabled={!input.trim() || isProcessing}
            variant="primary"
            size="sm"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
